import { useContext, useEffect, useState } from 'react';

import { useLogger } from '../services/LogService';
import { AppContext } from '../state/AppContextProvider';
import { ActionType } from '../state/GlobalState';
import {
  notifHelper,
  OneSignalConfig,
  OneSignalSDK,
} from '../services/onesignal/OneSignalService';
import { User } from '../model/models';
import { OneSignalWebImpl } from '../services/onesignal/OneSignalWebImpl';
import { OneSignalMobileImpl } from '../services/onesignal/OneSignalMobileImpl';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import OSNotification from 'onesignal-cordova-plugin/dist/OSNotification';
import { isPlatform } from '@ionic/react';
import { OpenedEvent } from 'onesignal-cordova-plugin/dist/models/NotificationOpened';

const ONE_SIGNAL_CONFIG: OneSignalConfig = {
  appId: process.env.REACT_APP_ONESIGNAL_ID || '',
  //subdomainName: 'http://localhost:3001',
  safari_web_id: process.env.REACT_APP_ONESIGNAL_SAFARI_ID || '',
  notifyButton: {
    enable: false,
  },
  allowLocalhostAsSecureOrigin: true,
};

export enum PushState {
  WAITING_FOR_INIT = -3, // inital state (not initialized)
  INIT_PENDING = -2, // initialisation is pending
  READY = -1, // onesignal ready, ready to fetch push state (enabled or not)
  ENABLED = 1, // onesignal ready and push is enabled
  DISABLED = 0, // onesignal ready and push is disabled
}

export interface UseOneSignalParams {
  refreshNotifs?: () => void;
  openNotif?: (notif: OSNotification) => void;
}

export function useOneSignal(params: UseOneSignalParams | null = null) {
  const logger = useLogger('OneSignal');

  const history = useHistory();

  const { state, dispatch } = useContext(AppContext);

  const intl = useIntl();

  const setPushSubscriptionState = (pushState: PushState) => {
    dispatch({ type: ActionType.SetPushState, payload: pushState });
  };

  // Init the Mobile or Web SDK implementation
  const [oneSignal] = useState<OneSignalSDK>(
    !isPlatform('capacitor')
      ? new OneSignalWebImpl()
      : new OneSignalMobileImpl()
  );

  const toggleUserPushNotification = (enabled: boolean, user: User) => {
    if (state.pushState < 0) {
      logger.info(
        `toggleUserPushNotification() invalid state: ${state.pushState}, will be ignored`
      );
      return;
    }
    setPushSubscriptionState(enabled ? PushState.ENABLED : PushState.DISABLED);
    if (enabled) {
      logger.info(
        'toggleUserPushNotification() Activating push notifications...'
      );
      enableUserPushNotification(user).then((enabled) => {
        setPushSubscriptionState(
          enabled ? PushState.ENABLED : PushState.DISABLED
        );
        if (!enabled) {
          const message = isPlatform('capacitor')
            ? intl.formatMessage({
                id: 'Mobile notifs are blocked',
              })
            : intl.formatMessage({
                id: 'Web notifs are blocked',
              });
          alert(message);
          logger.warn(
            `toggleUserPushNotification() Notification NOT ENABLED: ${message}`
          );
        } else {
          logger.info('toggleUserPushNotification() Notification ENABLED.');
        }
      });
    } else {
      logger.info(
        'toggleUserPushNotification() Deactivating push notifications...'
      );
      // Disable subscription
      oneSignal.setSubscription(false, user.username).then(() => {
        logger.info('toggleUserPushNotification() Notification DISABLED.');
        setPushSubscriptionState(PushState.DISABLED);
      });
    }
  };

  /**
   * Get permission, prompt user if required
   * @returns a promise with permissions
   */
  const getOrPromptPushPermission = (): Promise<any> => {
    return new Promise<any>((resolve, reject) => {
      oneSignal.getNotificationPermission((permission) => {
        if (permission === 'default') {
          oneSignal.showPrompt().then(
            () => {
              resolve('granted');
              /* oneSignal.getNotificationPermission((promptedPermission) => {
              resolve(promptedPermission);
            }); */
            },
            () => {
              resolve('denied');
            }
          );
        } else {
          resolve(permission);
        }
      });
    });
  };

  /**
   * Get device register
   */
  const getDeviceRegister = async () => {
    // try {
    //   console.log('ofnon');
    //   const externalUserId = await Promise.resolve(
    //     OneSignalReact.getExternalUserId()
    //   );
    //   console.log(externalUserId);
    //   if (externalUserId === state.user?.username) {
    //     const oneSignalUserId = await Promise.resolve(
    //       OneSignalReact.getUserId()
    //     );
    //     console.log('oneSignalUserId : ', oneSignalUserId);
    //     if (!oneSignalUserId) {
    //       return 'Il faut afficher la step Notification';
    //     } else {
    //       return 'Il ne faut pas afficher la step Notification';
    //     }
    //   }
    // } catch (e) {
    //   return 'erreur : ' + e;
    // }
  };

  const enableUserPushNotification = (user: User): Promise<boolean> => {
    logger.info('enableUserPushNotification');
    return new Promise<boolean>((resolve, reject) => {
      getOrPromptPushPermission().then((permission) => {
        if (permission === 'granted') {
          oneSignal.setSubscription(true, user.username);
          resolve(true);
        } else {
          oneSignal.setSubscription(false, user.username);
          resolve(false);
        }
      });
    });
  };

  const openNotificationHandler = (event: OpenedEvent) => {
    logger.info('openNotificationHandler : ', event);
    if (params?.refreshNotifs) {
      params.refreshNotifs();
    }
    if (params?.openNotif) {
      params.openNotif(event.notification);
    }
    if (isPlatform('ios')) {
      // On IOS, push notif links are not opening the target page
      const internal_link = notifHelper.toInternalLink(
        event.notification.launchURL || '/'
      );
      history.push(internal_link || '/');
    }
  };

  const notificationInForground = (event) => {
    logger.info('notificationInForground : ', event);
    event.complete(event.getNotification());
    if (params?.refreshNotifs) {
      params.refreshNotifs();
    }
  };

  useEffect(() => {
    /* Post init Onesignal hook */
    if (state.pushState === PushState.READY) {
      setPushSubscriptionState(PushState.INIT_PENDING);
      logger.debug('useEffect() post init OneSignal', state.pushState);
      oneSignal.isPushNotificationsEnabled().then((isEnabled) => {
        logger.log('useEffect() isPushNotificationsEnabled():', isEnabled);
        setPushSubscriptionState(
          isEnabled ? PushState.ENABLED : PushState.DISABLED
        );
        if (isEnabled) {
          // ensure that external id is set!
          oneSignal.setSubscription(true, state.user?.username || '');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pushState]);

  useEffect(() => {
    /* Init Onesignal hook */
    if (
      state.isConnected &&
      state.isLoggerReady &&
      state.pushState === PushState.WAITING_FOR_INIT
    ) {
      setPushSubscriptionState(PushState.INIT_PENDING);
      logger.debug('useEffect() init OneSignal', state.isLoggerReady);
      oneSignal.setLogger(logger);
      oneSignal
        .init(ONE_SIGNAL_CONFIG)
        .finally(() => {
          setPushSubscriptionState(PushState.READY);
          logger.info('One signal web push ready');
        })
        .then(() => {
          oneSignal.setNotificationOpenedHandler(openNotificationHandler);
          oneSignal.setNotificationWillShowInForegroundHandler(
            notificationInForground
          );
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isConnected, state.isLoggerReady]);

  return {
    oneSignal: oneSignal,
    getPushSubscriptionState: state.pushState,
    toggleUserPushNotification: toggleUserPushNotification,
    getDeviceRegister: getDeviceRegister,
  };
}
